import {
  isCoarsePointer,
  requestMobileView
} from '../../../../utils/mobileUtil';
import {
  onLayerClick,
  queryFeaturesNearPoint
} from '../../../../utils/mapHitUtil';
import {
  getObservationSpriteMatchExpression,
  getObservationClickedSpriteMatchExpression
} from '../../../../utils/observationTypeUtil';

export function setMapSources() {
  if (
    this.state.updateMapLayersCalled === false &&
    this.props.mapLayers.length > 0
  ) {
    this.setState({ updateMapLayersCalled: true });
  }
  if (!this.map.getSource('actualTracks')) {
    this.map.addSource('actualTracks', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  if (!this.map.getSource('alert')) {
    this.map.addSource('alert', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  if (!this.map.getSource(this.trackEndpointsSourceId)) {
    this.map.addSource(this.trackEndpointsSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  if (!this.map.getSource(this.trackPlaybackSourceId)) {
    this.map.addSource(this.trackPlaybackSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  if (!this.map.getSource(this.trackPlaybackProgressSourceId)) {
    this.map.addSource(this.trackPlaybackProgressSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  if (!this.map.getSource(this.trackPlaybackObservationSourceId)) {
    this.map.addSource(this.trackPlaybackObservationSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  // Clean up stale cluster layers for non-observation sources from prior hot-reloads.
  ['Agents', 'alert'].forEach(sourceName => {
    const ids = this.getClusterLayerIds(sourceName);
    if (this.map.getLayer(ids.count)) {
      this.map.removeLayer(ids.count);
    }
    if (this.map.getLayer(ids.circles)) {
      this.map.removeLayer(ids.circles);
    }
  });

  this.props.mapLayers.forEach(mapLayer => {
    const isDefaultProject = String(mapLayer.projectId) === 'default';
    const isDefaultObservationsLayer =
      isDefaultProject && mapLayer.name === 'Observations';
    const isDefaultTracksLayer = isDefaultProject && mapLayer.name === 'Tracks';
    const isDefaultAgentsLayer = isDefaultProject && mapLayer.name === 'Agents';
    const isDefaultMediaLayer = isDefaultProject && Number(mapLayer.id) === 3;

    if (!this.map.getSource(mapLayer.name)) {
      this.map.addSource(mapLayer.name, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      if (isDefaultProject) {
        if (isDefaultObservationsLayer) {
          this.setObservationLayer(mapLayer);
          this.setAlertLayer(mapLayer);
          const clusterVisibility =
            this.props.selectedLayers.includes('Observations') &&
            this.props.showObservationClusters
              ? 'visible'
              : 'none';
          this.addBackendObservationClusterLayers(clusterVisibility);
          const obsVisibility = this.props.selectedLayers.includes(
            'Observations'
          )
            ? 'visible'
            : 'none';
          this.addCamtrapClusterLayer(obsVisibility);
          this.ensureClusterSelectionIndicator();
          const observationHeatmapVisibility =
            this.props.selectedLayers.includes('Observations') &&
            this.props.showObservationHeatmap
              ? 'visible'
              : 'none';
          this.addHeatmapLayer(
            'Observations',
            observationHeatmapVisibility,
            undefined,
            this.props.observationHeatmapOpacity
          );
        } else if (isDefaultTracksLayer) {
          this.setActualTrackLayer(mapLayer);
          this.setTrackPlaybackLayer();
          this.setTracksLayer(mapLayer);
          const trackHeatmapVisibility =
            this.props.selectedLayers.includes('Tracks') &&
            this.props.showTrackHeatmap
              ? 'visible'
              : 'none';
          this.addHeatmapLayer(
            'Tracks',
            trackHeatmapVisibility,
            undefined,
            this.props.trackHeatmapOpacity
          );
          const trackFullStandardVisibility =
            this.props.selectedLayers.includes('Tracks') &&
            !this.props.showTrackHeatmap &&
            this.props.showTrackFullStandard
              ? 'visible'
              : 'none';
          this.addTracksFullStandardLayer(trackFullStandardVisibility);
          this.updateTracksFullStandardLayer();
          const trackFullColourVisibility =
            this.props.selectedLayers.includes('Tracks') &&
            !this.props.showTrackHeatmap &&
            this.props.showTrackFullColour
              ? 'visible'
              : 'none';
          this.addTracksFullColourLayer(trackFullColourVisibility);
          this.updateTracksFullColourLayer();
        } else if (isDefaultAgentsLayer) {
          this.setPersonsLayer(mapLayer);
        } else if (isDefaultMediaLayer) {
          this.setMediaLayer(mapLayer);
        }
      } else {
        this.setGeoJsonLayer(mapLayer);
      }
    }

    if (isDefaultTracksLayer && this.map.getSource(mapLayer.name)) {
      this.setActualTrackLayer(mapLayer);
      this.setTrackPlaybackLayer();
      if (!this.map.getLayer(mapLayer.name)) {
        this.setTracksLayer(mapLayer);
      }
      this.addTracksFullStandardLayer();
      this.updateTracksFullStandardLayer();
      this.addTracksFullColourLayer();
      this.updateTracksFullColourLayer();
    }
  });
}

export async function setObservationLayer(mapLayer) {
  this.map.addLayer({
    id: mapLayer.name,
    source: mapLayer.name,
    type: 'symbol',
    filter: [
      'all',
      ['!', ['has', 'point_count']],
      ['!', ['has', 'point_count_abbreviated']],
      ['!', ['has', 'count']],
      ['!', ['has', 'Count']],
      ['!=', ['get', 'EntityType'], 'cluster'],
      ['!=', ['get', 'EntityType'], 'cluster_clicked']
    ],
    layout: {
      visibility: 'visible',
      'icon-image': getObservationSpriteMatchExpression({
        includePhotoVariants: true
      }),
      // Clamp icon size so markers remain legible when zoomed out and
      // don't become oversized when zoomed in.
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.16, 22, 0.5],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    },
    paint: {}
  });
  this.map.addLayer({
    id: mapLayer.name + '_clicked',
    source: mapLayer.name,
    type: 'symbol',
    filter: [
      'all',
      ['!', ['has', 'point_count']],
      ['!', ['has', 'point_count_abbreviated']],
      ['!', ['has', 'count']],
      ['!', ['has', 'Count']],
      ['!=', ['get', 'EntityType'], 'cluster'],
      ['!=', ['get', 'EntityType'], 'cluster_clicked']
    ],
    layout: {
      visibility: 'visible',
      'icon-image': getObservationClickedSpriteMatchExpression(),
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.2, 22, 0.55],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    },
    paint: {}
  });

  if (this.map.getLayer(mapLayer.name + '_clicked')) {
    this.map.moveLayer(mapLayer.name + '_clicked');
  }
  if (this.map.getLayer('Agents')) {
    this.map.moveLayer('Agents');
  }

  if (!this.state.observationClickSet) {
    onLayerClick(this.map, mapLayer.name, e => {
      let f = this.map.queryRenderedFeatures(e.point);
      // Desktop: the observation must be the top-most feature under the cursor.
      // Touch: the nearest observation wins, a finger covers several layers.
      if (
        isCoarsePointer() ||
        (f.length && f[0].layer.source === mapLayer.name)
      ) {
        if (
          e.features[0].properties &&
          e.features[0].properties.overlap_cluster
        ) {
          const overlapKey = e.features[0].properties.overlap_key;
          if (this.activeObservationSpiderKey === overlapKey) {
            this.activeObservationSpiderKey = null;
            this.setClusterSelectionFilter(null);
            if (this.props.clearCamtrapFocus) this.props.clearCamtrapFocus();
          } else {
            const group = overlapKey
              ? this.overlapObservationGroups[overlapKey] || []
              : [];
            const memberIds = group
              .map(f => String((f.properties && f.properties.EntityId) || ''))
              .filter(Boolean);
            this.activeObservationSpiderKey = overlapKey;
            this.setClusterSelectionFilter(overlapKey, e.features[0]);
            this.props.clearSelectedItem();
            this.props.clearEntireTrack();
            if (memberIds.length > 0 && this.props.fetchClusterResults) {
              this._camtrapFetchPending = true;
              this.props.fetchClusterResults(memberIds);
            }
          }
          return;
        }
        const id = String(
          (e.features[0].properties && e.features[0].properties.EntityId) || ''
        ).trim();
        if (!id) return;

        // Mobile: show the selected observation, highlighted, in the results.
        requestMobileView('results');

        // Check if already in timeline (robust id matching in Observation payload).
        const observationInTimeline = this.props.timelineResults.find(
          result => {
            const content = Array.isArray(result?.extracted?.content)
              ? result.extracted.content
              : [];
            const observationEntry = content.find(
              entry => entry && entry.Observation
            );
            const observation =
              observationEntry && observationEntry.Observation;
            const props = (observation && observation.properties) || {};
            const candidateIds = [
              observation && observation.observationId,
              observation && observation.ObservationId,
              observation && observation.EntityId,
              props.observationId,
              props.ObservationId,
              props.EntityId
            ]
              .map(value => String(value || '').trim())
              .filter(Boolean);
            return candidateIds.includes(id);
          }
        );

        if (observationInTimeline) {
          const observationPayload = (
            observationInTimeline.extracted.content.find(c => c.Observation) ||
            {}
          ).Observation;
          this.props.setSelectedItem(
            observationPayload
              ? {
                  ...observationPayload,
                  observationId:
                    observationPayload.observationId ||
                    observationPayload.ObservationId ||
                    observationPayload.EntityId
                }
              : observationPayload
          );
        } else {
          this.props.getSingleItem(id);
        }
        this.props.clearEntireTrack();
      }
    });

    this.map.on('click', mapLayer.name + '_clicked', e => {
      const f = this.map.queryRenderedFeatures(e.point, {
        layers: [mapLayer.name + '_clicked']
      });
      if (f.length) {
        this.props.clearSelectedItem();
      }
    });

    onLayerClick(this.map, 'Observations_camtrap_clusters', e => {
      if (!e.features || !e.features.length) return;
      const overlapKey =
        e.features[0].properties && e.features[0].properties.overlap_key;
      if (this.activeObservationSpiderKey === overlapKey) {
        this.activeObservationSpiderKey = null;
        this.setClusterSelectionFilter(null);
        if (this.props.clearCamtrapFocus) this.props.clearCamtrapFocus();
        return;
      }
      const group = overlapKey
        ? this.overlapObservationGroups[overlapKey] || []
        : [];
      const memberIds = group
        .map(f => String((f.properties && f.properties.EntityId) || ''))
        .filter(Boolean);
      this.activeObservationSpiderKey = overlapKey;
      this.setClusterSelectionFilter(overlapKey, e.features[0]);
      this.props.clearSelectedItem();
      this.props.clearEntireTrack();
      if (memberIds.length > 0 && this.props.fetchClusterResults) {
        this.props.fetchClusterResults(memberIds);
      }
    });

    this.map.on('mouseenter', 'Observations_camtrap_clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'Observations_camtrap_clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.setState({ observationClickSet: true });
  }

  if (!this.spiderClickHandlersSet) {
    this.map.on('click', e => {
      const featuresAtPoint = queryFeaturesNearPoint(this.map, e.point, [
        'Observations',
        'Observations_clicked',
        'Observations_backend_clusters',
        'Observations_backend_cluster_count',
        'Observations_camtrap_clusters',
        'Observations_camtrap_cluster_count'
      ]);
      if (!featuresAtPoint.length) {
        this.activeObservationSpiderKey = null;
        this.setClusterSelectionFilter(null);
        if (this.props.clearCamtrapFocus) this.props.clearCamtrapFocus();
      }
    });

    this.spiderClickHandlersSet = true;
  }

  this.map.on('mouseenter', [mapLayer.name, mapLayer.name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = 'pointer';
  });

  this.map.on('mouseleave', [mapLayer.name, mapLayer.name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = '';
  });
}

export async function setAlertLayer(mapLayer) {
  let name = 'alert';
  this.map.addLayer({
    id: name,
    source: name,
    type: 'symbol',
    filter: ['!', ['has', 'point_count']],
    layout: {
      visibility: 'none',
      'icon-image': getObservationSpriteMatchExpression({
        includePhotoVariants: true
      }),
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.12, 22, 0.4],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    },
    paint: {}
  });

  this.map.addLayer({
    id: name + '_clicked',
    source: name,
    type: 'symbol',
    filter: ['!', ['has', 'point_count']],
    layout: {
      visibility: 'visible',
      'icon-image': getObservationClickedSpriteMatchExpression(),
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.16, 22, 0.48],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    },
    paint: {}
  });

  if (this.map.getLayer(name + '_clicked')) {
    this.map.moveLayer(name + '_clicked');
  }

  if (!this.state.alertClickSet) {
    this.map.on('click', name, e => {
      let f = this.map.queryRenderedFeatures(e.point);
      if (f.length && f[0].layer.source === name) {
        var id = e.features[0].properties.EntityId;

        // Check if is already in timeline
        this.props.getSingleItem(id);
        this.props.clearEntireTrack();
      }
    });

    this.map.on('click', name + '_clicked', e => {
      let f = this.map.queryRenderedFeatures(e.point);
      if (f.length && f[0].layer.source === name) {
        this.props.clearSelectedItem();
      }
    });

    this.setState({ alertClickSet: true });
  }

  this.map.on('mouseenter', [name, name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = 'pointer';
  });

  this.map.on('mouseleave', [name, name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = '';
  });
}

export async function setTracksLayer(mapLayer) {
  // Centerpoint icons only ever show in 'centerpoints' mode; heatmap,
  // full-standard and full-colour modes all replace them (see
  // setLayerMarkersVisibility / applyTrackFullStandardVisibility /
  // applyTrackFullColourVisibility in MapComponent.js).
  const iconVisibility =
    this.props.selectedLayers.includes(mapLayer.name) &&
    !this.props.showTrackHeatmap &&
    !this.props.showTrackFullStandard &&
    !this.props.showTrackFullColour
      ? 'visible'
      : 'none';

  this.map.addLayer({
    id: mapLayer.name,
    source: mapLayer.name,
    type: 'symbol',
    filter: [
      '==',
      ['index-of', '_clicked', ['coalesce', ['get', 'EntityType'], '']],
      -1
    ],
    layout: {
      visibility: iconVisibility,
      'icon-image': [
        'match',
        ['get', 'EntityType'],
        ['track/onDuty'],
        'onduty',
        ['track/onFootPatrol'],
        'onfootpatrol',
        ['track/onVehiclePatrol'],
        'onvehiclepatrol',
        ['track/onAirPatrol'],
        'onairpatrol',
        ['track/onHumanSpoor'],
        'onhumanspoor',
        ['track/onAnimalSpoor'],
        'onanimalspoor',
        ['track/onSafari'],
        'onsafari',
        ['track/onCensus'],
        'oncensus',
        ['track/onK9Patrol'],
        'onk9patrol',
        ['track/onFenceInspection'],
        'onfenceinspection',
        ['track/onPerimeterTrack'],
        'onperimetertrack',
        ['track/onSnareSweep'],
        'onsnaresweep',
        ['track/onSearch'],
        'onsearch',
        ['track/onBoatPatrol'],
        'onboatpatrol',
        ['track/onDronePatrol'],
        'ondronepatrol',
        ['track/onFastResponse'],
        'onfastresponse',
        ['track/onTraining'],
        'ontraining',
        ['track/onBikePatrol'],
        'onbikepatrol',
        'onfootpatrol'
      ],
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.19, 22, 0.6],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    }
  });

  this.map.addLayer({
    id: mapLayer.name + '_clicked',
    source: mapLayer.name,
    type: 'symbol',
    filter: [
      '>=',
      ['index-of', '_clicked', ['coalesce', ['get', 'EntityType'], '']],
      0
    ],
    layout: {
      visibility: iconVisibility,
      'icon-image': [
        'match',
        ['get', 'EntityType'],
        ['track/onDuty_clicked'],
        'onduty_clicked',
        ['track/onFootPatrol_clicked'],
        'onfootpatrol_clicked',
        ['track/onVehiclePatrol_clicked'],
        'onvehiclepatrol_clicked',
        ['track/onAirPatrol_clicked'],
        'onairpatrol_clicked',
        ['track/onHumanSpoor_clicked'],
        'onhumanspoor_clicked',
        ['track/onAnimalSpoor_clicked'],
        'onanimalspoor_clicked',
        ['track/onSafari_clicked'],
        'onsafari_clicked',
        ['track/onCensus_clicked'],
        'oncensus_clicked',
        ['track/onK9Patrol_clicked'],
        'onk9patrol_clicked',
        ['track/onFenceInspection_clicked'],
        'onfenceinspection_clicked',
        ['track/onPerimeterTrack_clicked'],
        'onperimetertrack_clicked',
        ['track/onSnareSweep_clicked'],
        'onsnaresweep_clicked',
        ['track/onSearch_clicked'],
        'onsearch_clicked',
        ['track/onBoatPatrol_clicked'],
        'onboatpatrol_clicked',
        ['track/onDronePatrol_clicked'],
        'ondronepatrol_clicked',
        ['track/onFastResponse_clicked'],
        'onfastresponse_clicked',
        ['track/onTraining_clicked'],
        'ontraining_clicked',
        ['track/onBikePatrol_clicked'],
        'onbikepatrol_clicked',
        ['cluster_clicked'],
        'onfootpatrol_clicked',
        'onfootpatrol_clicked'
      ],
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.24, 22, 0.66],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true
    }
  });

  if (this.map.getLayer(mapLayer.name + '_clicked')) {
    this.map.moveLayer(mapLayer.name + '_clicked');

    if (this.map.getLayer('Observations_clicked')) {
      this.map.moveLayer(mapLayer.name, 'Observations_clicked');
    }
  }
  if (this.map.getLayer(`${mapLayer.name}_lines`)) {
    this.map.moveLayer(`${mapLayer.name}_lines`);
  }
  if (this.map.getLayer('Agents')) {
    this.map.moveLayer('Agents');
  }

  if (!this.state.trackClickSet) {
    this.map.on('click', mapLayer.name, e => {
      let f = this.map.queryRenderedFeatures(e.point);
      if (f.length && f[0].layer.source === mapLayer.name) {
        const clickedFeature = e.features[0] || {};
        const clickedProperties = clickedFeature.properties || {};
        const id =
          clickedProperties.EntityId ||
          clickedProperties.featureId ||
          clickedProperties.trackId ||
          clickedFeature.featureId;
        if (!id) return;
        // Check if the track is already present in timeline results.
        const normalizedId = String(id).trim();
        const trackInTimeline = this.props.timelineResults.find(result => {
          const content = Array.isArray(result?.extracted?.content)
            ? result.extracted.content
            : [];
          const geoFeatureEntry = content.find(
            entry => entry && entry.GeoFeature
          );
          const geoFeature = geoFeatureEntry && geoFeatureEntry.GeoFeature;
          const props = (geoFeature && geoFeature.properties) || {};
          const candidateIds = [
            geoFeature && geoFeature.featureId,
            geoFeature && geoFeature.trackId,
            geoFeature && geoFeature.EntityId,
            props.EntityId,
            props.featureId,
            props.trackId
          ]
            .map(value => String(value || '').trim())
            .filter(Boolean);
          return candidateIds.includes(normalizedId);
        });
        this.setSelectedTrackItem(id, trackInTimeline, clickedFeature);
      }
    });

    this.map.on('click', mapLayer.name + '_clicked', e => {
      const f = this.map.queryRenderedFeatures(e.point, {
        layers: [mapLayer.name + '_clicked']
      });
      if (f.length) {
        this.props.clearSelectedItem();
      }
    });

    // Clicking empty map space while a single track is open closes it -
    // same effect as the "Close track" button in the observation list (see
    // ObservationList.closeSelectedTrackView), which likewise just clears
    // the shared selectedItem and lets everything else (actualTracks
    // re-render, ObservationList's own track-context cleanup) react to
    // that. Skipped when the click actually lands on the track's own line/
    // dots (actualTracks*) or its icon (mapLayer.name*), so those layers'
    // own click handlers (above, and actualTracks.js) get to handle it.
    this.map.on('click', e => {
      if (!this.getSelectedTrackId(this.props.selectedItem)) return;
      const hitTrackLayers = this.map.queryRenderedFeatures(e.point, {
        layers: [
          'actualTracks',
          'actualTracks_outline',
          'actualTracks_points',
          mapLayer.name,
          mapLayer.name + '_clicked'
        ].filter(layerId => this.map.getLayer(layerId))
      });
      if (!hitTrackLayers.length && this.props.clearSelectedItem) {
        this.props.clearSelectedItem();
      }
    });

    this.setState({ trackClickSet: true });
  }

  this.map.on('mouseenter', [mapLayer.name, mapLayer.name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = 'pointer';
  });

  this.map.on('mouseleave', [mapLayer.name, mapLayer.name + '_clicked'], () => {
    this.map.getCanvas().style.cursor = '';
  });
}
